import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Plus, Eye, Pencil, Trash2 } from 'lucide-react';
import { locationHubService } from '../../services/locationHubService';
import Alert from '../common/Alert';
import {
  PageLayout,
  Button,
  DataTable,
  FilterPanel,
  Pagination,
} from '../common/index.js';

const LocationHubList = () => {
  const navigate = useNavigate();
  const [hubs, setHubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filterValues, setFilterValues] = useState({
    search: '',
    location: '',
    state: '',
  });
  const perPage = 10;

  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);
      fetchHubs(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [filterValues]);

  useEffect(() => {
    fetchHubs(currentPage);
  }, [currentPage]);

  const fetchHubs = async (page = currentPage) => {
    try {
      setLoading(true);
      const filters = {};
      if (filterValues.search) filters.search = filterValues.search;
      if (filterValues.location) filters.location = filterValues.location;
      if (filterValues.state) filters.state = filterValues.state;

      const response = await locationHubService.getAllHubs(page, perPage, filters);
      const hubsData = response.data.hubs || response.data.data || [];
      setHubs(hubsData);

      const pagination = response.data.pagination || {};
      setTotalPages(pagination.pages || 1);
    } catch (error) {
      console.error('Error fetching hubs:', error);
      Alert.error(error.response?.data?.error || 'Failed to fetch location hubs');
      setHubs([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (hubId) => {
    const confirmed = await Alert.confirm(
      'Are you sure you want to delete this location hub?',
      'Delete Location Hub'
    );
    if (!confirmed) return;

    try {
      await locationHubService.deleteHub(hubId);
      Alert.success('Location hub deleted successfully');

      if (hubs.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      } else {
        fetchHubs();
      }
    } catch (error) {
      Alert.error(error.response?.data?.error || 'Failed to delete location hub');
    }
  };

  const columns = [
    {
      key: 'hub_id',
      label: 'Hub ID',
      render: (hub) => (
        <button
          onClick={() => navigate(`/location-hubs/view/${hub.hub_id}`)}
          className="font-medium text-brand hover:underline text-left font-mono"
        >
          {hub.hub_id}
        </button>
      ),
    },
    { key: 'address', label: 'Address' },
    { key: 'location', label: 'Destination' },
    { key: 'location_code', label: 'Code' },
    { key: 'state', label: 'State' },
    {
      key: 'destination_type',
      label: 'Type',
      render: (hub) =>
        hub.destination_type === 'middle_destination' ? 'Middle' : 'Branch',
    },
    {
      key: 'created_by',
      label: 'Created By',
      render: (hub) => hub.created_by || 'N/A',
    },
  ];

  const getActions = (hub) => [
    {
      label: 'View Details',
      icon: Eye,
      onClick: () => navigate(`/location-hubs/view/${hub.hub_id}`),
    },
    {
      label: 'Edit Hub',
      icon: Pencil,
      onClick: () => navigate(`/location-hubs/edit/${hub.hub_id}`),
    },
    {
      label: 'Delete Hub',
      icon: Trash2,
      variant: 'danger',
      onClick: () => handleDelete(hub.hub_id),
    },
  ];

  return (
    <PageLayout
      title="Destination Hubs"
      icon={MapPin}
      maxWidth="max-w-full"
      actions={
        <Button icon={Plus} onClick={() => navigate('/location-hubs/add')}>
          Add Hub
        </Button>
      }
    >
      <FilterPanel
        filters={[
          { key: 'search', label: 'Search', placeholder: 'Hub ID or address...' },
          { key: 'location', label: 'Destination', placeholder: 'Destination...' },
          {
            key: 'state',
            label: 'State',
            type: 'select',
            options: [
              { value: 'Mumbai', label: 'Mumbai' },
              { value: 'Goa', label: 'Goa' },
            ],
          },
        ]}
        values={filterValues}
        onChange={setFilterValues}
        onClear={() => setFilterValues({ search: '', location: '', state: '' })}
      />

      <DataTable
        columns={columns}
        data={hubs}
        loading={loading}
        emptyMessage="No location hubs found"
        rowKey="id"
        actions={getActions}
      />

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />
    </PageLayout>
  );
};

export default LocationHubList;
